import { QueryClient } from "@tanstack/react-query";
import { z } from "zod";

import {
  fetchAllResultsWithError,
  fetchWithError,
  useParsedQueries,
  useParsedQuery,
} from "@nens/react-query-utils";
import { combineUrlAndParams, removeNullUndefined } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { paginatedSchema } from "../pagination";
import { uuidSchema } from "../uuid";

import { organisationSchema } from "./organisations";
import { fetchAndCacheRaster } from "./rasters";

export const scenarioListItemSchema = z.object({
  url: z.string(),
  uuid: z.string(),
  name: z.string(),
  organisation: organisationSchema,
  project: z.string().nullable(),
  description: z.string().nullable(),
  extra_metadata: z.record(z.any()),
  access_modifier: z.string(),
  created: lizardDateSchema,
  last_modified: lizardDateSchema.nullable(),
  simulation_identifier: z.string().nullable(),
  simulation_start: lizardDateSchema.nullable(),
  simulation_end: lizardDateSchema.nullable(),
  supplier: z.string().nullable(),
  source: z.string(),
  model_identifier: z.string().nullable(),
  model_revision: z.string().nullable(),
  model_name: z.string().nullable(),
  has_raw_results: z.boolean(),
  total_size: z.number(),
  spatial_bounds: z
    .object({
      west: z.number(),
      east: z.number(),
      north: z.number(),
      south: z.number(),
    })
    .nullable(),
});

export const scenarioResultListItemSchema = z.object({
  url: z.string(),
  id: z.number(),
  name: z.string(),
  code: z.string(),
  description: z.string().nullable(),
  family: z.union([
    z.literal("Raw"),
    z.literal("Basic"),
    z.literal("Arrival"),
    z.literal("Damage"),
    z.literal("Tiles"),
    z.literal("Vulnerable_Buildings"),
    z.literal("Preview"),
  ]),
  raster: uuidSchema,
  attachment_url: z.string().nullable(),
});

export type ScenarioListItem = z.infer<typeof scenarioListItemSchema>;

export const scenarioListSchema = paginatedSchema(scenarioListItemSchema);
export type ScenarioList = z.infer<typeof scenarioListSchema>;

export const scenarioSchema = scenarioListItemSchema;
export type Scenario = z.infer<typeof scenarioSchema>;

export type ScenarioResultListItem = z.infer<typeof scenarioResultListItemSchema>;

export const scenarioResultListSchema = paginatedSchema(scenarioResultListItemSchema);
export type ScenarioResultList = z.infer<typeof scenarioResultListSchema>;

export const scenarioResultSchema = scenarioResultListItemSchema;
export type ScenarioResult = z.infer<typeof scenarioResultSchema>;

export function useScenarios(params = {}) {
  const url = combineUrlAndParams("/api/v4/scenarios/", params);
  return useParsedQuery<ScenarioList>(
    ["lizard-api-v4", "scenarios", url],
    () => fetchWithError(url),
    (json: unknown) => scenarioListSchema.parse(json)
  );
}

export function useScenario(uuid: string) {
  return useParsedQuery<Scenario>(
    ["lizard-api-v4", "scenarios", uuid],
    () => fetchWithError(`/api/v4/scenarios/${uuid}/`),
    (json: unknown) => scenarioSchema.parse(json)
  );
}

// Use this outside components, so the scenario can still
// be stored in cache.
export async function fetchAndCacheScenario(
  queryClient: QueryClient,
  uuid: string
): Promise<Scenario | null> {
  try {
    const response = await fetchWithError(`/api/v4/scenarios/${uuid}/`);
    const scenario = scenarioSchema.parse(response);

    queryClient.setQueryData(["lizard-api-v4", "scenarios", uuid], scenario);
    return scenario;
  } catch (e) {
    return null;
  }
}

export function useMultipleScenarios(uuids: string[]) {
  return useParsedQueries(
    uuids.map((uuid) => {
      return {
        queryKey: ["lizard-api-v4", "scenarios", uuid],
        fetchFunction: () => fetchWithError(`/api/v4/scenarios/${uuid}/`),
        parseFunction: (json: unknown) => scenarioSchema.parse(json),
      };
    })
  );
}

export function useScenarioResults(uuid: string, params = {}) {
  const url = combineUrlAndParams(`/api/v4/scenarios/${uuid}/results/`, params);
  return useParsedQuery<ScenarioResultList>(
    ["lizard-api-v4", "scenarioresults", url],
    () => fetchWithError(url),
    (json: unknown) => scenarioResultListSchema.parse(json)
  );
}

// Use this outside components, so the scenario can still
// be stored in cache.
export async function fetchAndCacheScenarioResults(
  queryClient: QueryClient,
  uuid: string,
  params = {}
): Promise<ScenarioResultList | null> {
  try {
    const url = combineUrlAndParams(`/api/v4/scenarios/${uuid}/results/`, params);
    const results = await fetchAllResultsWithError(url);

    const scenarioResultList = scenarioResultListSchema.parse({
      count: results.length,
      previous: null,
      next: null,
      results,
    });

    queryClient.setQueryData(["lizard-api-v4", "scenarioresults", url], scenarioResultList);
    return scenarioResultList;
  } catch (e) {
    return null;
  }
}

export function useScenarioResult(uuid: string, id: number) {
  return useParsedQuery<ScenarioResult>(
    ["lizard-api-v4", "scenarioresults", uuid, id],
    () => fetchWithError(`/api/v4/scenarios/${uuid}/results/${id}/`),
    (json: unknown) => scenarioResultSchema.parse(json)
  );
}

export async function getScenarioResultRastersFromScenario(
  queryClient: QueryClient,
  scenarioUuid: string
) {
  // Fetch scenario results
  const resultList = await fetchAndCacheScenarioResults(queryClient, scenarioUuid);

  // Now check which rasters are fetchable by this user
  const rasterUuids = removeNullUndefined(
    await Promise.all(
      (resultList
        ? removeNullUndefined(resultList.results.map((result) => result.raster))
        : []
      ).map((uuid) => fetchAndCacheRaster(queryClient, uuid))
    )
  ).map((raster) => raster.uuid);

  return rasterUuids;
}
